<template>
  <div class="merchant-container">
    <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></loading>
    <div class="side-nav">
      <MerchantSideNav />
      <MenuToggle />
    </div>
    <div class="merchant-body transaction-body">
      <header>
        <MerchantHeader />
      </header>
      <div class="dashboard-content">
        <div class="row mt-5">
          <h3>Withdraw</h3>
        </div>

        <!-- BALANCE + REQUEST FORM -->
        <div class="row">
          <div class="mb-3 col-12 col-lg-12 col-xl-4 pl-0">
            <div class="card-style h-100">
              <span>Available Balance</span>
              <h1>{{ currencySymbol }} {{ money(availableFunds) }}</h1>
              <div class="funds-note" v-if="num(funds.on_hold) > 0">
                {{ currencySymbol }} {{ money(funds.on_hold) }} on hold
                <template v-if="funds.hold_hours">
                  &middot; released {{ funds.hold_hours }}h after payment
                </template>
              </div>

              <div class="limits mt-3">
                <div class="limit-row">
                  <span>Minimum</span>
                  <strong>{{ limits.min_amount != null ? currencySymbol + ' ' + money(limits.min_amount) : 'No limit' }}</strong>
                </div>
                <div class="limit-row">
                  <span>Maximum</span>
                  <strong>{{ limits.max_amount != null ? currencySymbol + ' ' + money(limits.max_amount) : 'No limit' }}</strong>
                </div>
                <div class="limit-row" v-if="limits.daily_amount != null || usage.daily_amount != null">
                  <span>Today</span>
                  <strong>
                    {{ currencySymbol }} {{ money(usage.daily_amount) }}
                    <template v-if="limits.daily_amount != null">/ {{ money(limits.daily_amount) }}</template>
                  </strong>
                </div>
                <div class="limit-row" v-if="limits.monthly_amount != null || usage.monthly_amount != null">
                  <span>This month</span>
                  <strong>
                    {{ currencySymbol }} {{ money(usage.monthly_amount) }}
                    <template v-if="limits.monthly_amount != null">/ {{ money(limits.monthly_amount) }}</template>
                  </strong>
                </div>
                <div class="limit-row" v-if="limits.daily_count != null || limits.monthly_count != null">
                  <span>Requests</span>
                  <strong>
                    {{ num(usage.daily_count) }}<template v-if="limits.daily_count != null">/{{ limits.daily_count }}</template> today
                    &middot;
                    {{ num(usage.monthly_count) }}<template v-if="limits.monthly_count != null">/{{ limits.monthly_count }}</template> month
                  </strong>
                </div>
                <div class="limit-row" v-if="withdrawLimits.tier">
                  <span>Tier</span>
                  <strong class="capitalize">{{ withdrawLimits.tier }}</strong>
                </div>
              </div>

              <!-- Fee slabs, labelled by the API in the merchant's own display currency. -->
              <div class="slabs mt-3" v-if="feeSlabs.length">
                <div class="slabs-head">
                  Withdrawal fee<template v-if="feeDisplayCurrency"> ({{ feeDisplayCurrency }})</template>
                </div>
                <div class="slab-row" v-for="(sl, i) in feeSlabs" :key="'sl' + i">
                  <span>{{ sl.label }}</span>
                  <strong>{{ sl.fee_label }}</strong>
                </div>
                <div class="slab-foot" v-if="fallbackPercent !== null">
                  Amounts outside these bands are charged {{ fallbackPercent }}%.
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-12 col-xl-8 pl-0">
            <div class="card-style h-100">
              <span>Request a Withdrawal</span>

              <div class="row mt-2" v-if="!canWithdraw">
                <div class="col-12">
                  <p class="notice">{{ blockedReason }}</p>
                </div>
              </div>

              <div class="row mt-2" v-if="!myBanks.length">
                <div class="col-12">
                  <p class="notice">
                    You have no settlement bank saved.
                    <router-link :to="{ name: 'merchant-bank' }">Add a bank account</router-link>
                    to choose where payouts are sent. Until then the account on your merchant
                    KYC is used.
                  </p>
                </div>
              </div>

              <b-form @submit.stop.prevent="submitRequest">
                <!-- Row 1: the two fields that decide the payout, side by side.
                     Row 2: remarks, which is free text and benefits from the full width. -->
                <div class="row mt-2">
                  <div class="mb-3 col-12 col-lg-6 position-relative">
                    <b-form-select
                      v-model="form.bank"
                      :options="bankOptions"
                      :disabled="!canWithdraw"
                      @change="onInputsChanged"
                    ></b-form-select>
                    <span class="position-absolute">Withdraw To *</span>
                    <span class="text-danger" v-if="errors.bank">{{ errors.bank }}</span>
                  </div>
                  <div class="mb-3 col-12 col-lg-6 position-relative">
                    <b-form-input
                      v-model="form.amount"
                      placeholder="0.00"
                      :disabled="!canWithdraw"
                      oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                      @blur="onInputsChanged"
                    ></b-form-input>
                    <span class="position-absolute">Amount ({{ balance.currency || 'USD' }}) *</span>
                    <span class="text-danger" v-if="errors.amount">{{ errors.amount }}</span>
                  </div>
                </div>
                <div class="row">
                  <div class="mb-3 col-12 position-relative">
                    <b-form-input
                      v-model="form.remarks"
                      placeholder="Optional note for the Admin"
                      :disabled="!canWithdraw"
                    ></b-form-input>
                    <span class="position-absolute">Remarks</span>
                    <span class="text-danger" v-if="errors.remarks">{{ errors.remarks }}</span>
                  </div>
                </div>

                <!-- QUOTE: priced by the server the moment the amount loses focus. -->
                <div class="quote-box" v-if="quoteLoading">
                  <b-spinner small class="mr-2"></b-spinner>
                  <span class="muted">Pricing this withdrawal...</span>
                </div>
                <div class="quote-box is-error" v-else-if="quoteError">{{ quoteError }}</div>
                <div class="quote-box" v-else-if="hasQuote">
                  <div class="quote-head">
                    <span class="quote-title">Before you confirm</span>
                    <span class="quote-dest" v-if="quoteDestination.bank_name">
                      to {{ quoteDestination.bank_name }}
                      <template v-if="quoteDestination.account_number_masked">
                        &middot; {{ quoteDestination.account_number_masked }}
                      </template>
                    </span>
                  </div>
                  <!-- The server sends its own formatted `display` string for every line,
                       so the figures on screen are the server's, not a second calculation
                       that could round differently. -->
                  <div
                    class="quote-row"
                    v-for="(r, i) in quoteSummary"
                    :key="'q' + i"
                    :class="quoteRowClass(r)"
                  >
                    <span class="quote-label">{{ r.label }}</span>
                    <span class="quote-value">{{ r.display }}</span>
                  </div>
                  <!-- The bank's own currency. Shown only when the server has not already
                       priced a payout in it, and marked as an estimate because the rate
                       used here is the app's cached table, not the settlement rate. -->
                  <div class="quote-row is-local" v-if="quoteLocal">
                    <span class="quote-label">
                      Approximately, in {{ quoteLocal.code }}
                      <small v-if="quoteLocal.indicative">estimate</small>
                    </span>
                    <span class="quote-value">{{ quoteLocal.text }}</span>
                  </div>
                  <div class="quote-foot" v-if="quoteBalanceAfter !== null">
                    Balance after this withdrawal:
                    <strong>{{ currencySymbol }} {{ money(quoteBalanceAfter) }}</strong>
                  </div>
                  <div class="quote-blockers" v-if="quoteBlockers.length">
                    <div v-for="(b, i) in quoteBlockers" :key="'b' + i">{{ blockerText(b) }}</div>
                  </div>
                </div>

                <div class="row align-items-center">
                  <div class="col-12 col-lg-4">
                    <b-button variant="primary" type="submit" :disabled="submitDisabled">
                      <b-spinner small v-if="isSubmitting" class="mr-2"></b-spinner>
                      {{ isSubmitting ? "Submitting..." : "Request Withdraw" }}
                    </b-button>
                  </div>
                  <div class="col-12 col-lg-8">
                    <small class="muted" v-if="maxRequestable != null">
                      You can request up to
                      <strong>{{ currencySymbol }} {{ money(maxRequestable) }}</strong> right now.
                    </small>
                  </div>
                </div>
              </b-form>
            </div>
          </div>
        </div>

        <!-- ANALYTICS -->
        <div class="row mt-4">
          <div class="col-12 col-lg-6 col-xl-3 mb-3 pl-0">
            <div class="card-style">
              <span>Total Withdrawn</span>
              <h2>{{ currencySymbol }} {{ money(wTotals.completed_amount) }}</h2>
              <small class="muted">{{ num(wTotals.count) }} requests</small>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 mb-3 pl-0">
            <div class="card-style">
              <span>Pending Amount</span>
              <h2 class="pending-text">{{ currencySymbol }} {{ money(wTotals.pending_amount) }}</h2>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 mb-3 pl-0">
            <div class="card-style">
              <span>Average / Largest</span>
              <h2>{{ currencySymbol }} {{ money(wTotals.average_amount) }}</h2>
              <small class="muted">largest {{ currencySymbol }} {{ money(wTotals.largest_amount) }}</small>
            </div>
          </div>
          <div class="col-12 col-lg-6 col-xl-3 mb-3 pl-0">
            <div class="card-style">
              <span>Completion Rate</span>
              <h2 class="success">{{ num(wTotals.completion_rate) }}%</h2>
              <div class="rate-bar"><div class="rate-fill" :style="{ width: completionWidth }"></div></div>
            </div>
          </div>
        </div>

        <!-- CHART + STATUS BREAKDOWN -->
        <div class="row mb-4">
          <div class="col-12 col-xl-7 mb-3 pl-0">
            <div class="card-style">
              <h3 class="mb-0">Withdrawals Over Time</h3>
              <small class="muted" v-if="wRange.start">{{ wRange.start }} to {{ wRange.end }}</small>
              <div v-if="!chart.points.length" class="chart-empty">No withdrawal data yet.</div>
              <div v-else class="chart-wrap">
                <svg :viewBox="'0 0 ' + chart.w + ' ' + chart.h" class="chart-svg" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="wdFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#0066b3" stop-opacity="0.28" />
                      <stop offset="100%" stop-color="#0066b3" stop-opacity="0.02" />
                    </linearGradient>
                  </defs>
                  <line
                    v-for="(g, i) in chart.grid" :key="'g' + i"
                    :x1="chart.padL" :x2="chart.w - chart.padR" :y1="g.y" :y2="g.y"
                    stroke="#eceff3" stroke-width="1"
                  />
                  <text
                    v-for="(g, i) in chart.grid" :key="'t' + i"
                    :x="chart.padL - 8" :y="g.y + 4" text-anchor="end" class="axis-text"
                  >{{ g.label }}</text>
                  <polygon :points="chart.area" fill="url(#wdFill)" />
                  <polyline :points="chart.line" fill="none" stroke="#0066b3" stroke-width="2"
                    stroke-linejoin="round" stroke-linecap="round" />
                  <circle
                    v-for="(p, i) in chart.points" :key="'p' + i"
                    :cx="p.x" :cy="p.y" :r="p.value > 0 ? 3.5 : 2"
                    :fill="p.value > 0 ? '#0066b3' : '#c9d3dd'"
                  >
                    <title>{{ p.date }} — {{ currencySymbol }} {{ money(p.value) }} ({{ p.count }})</title>
                  </circle>
                  <text
                    v-for="(p, i) in chart.xLabels" :key="'x' + i"
                    :x="p.x" :y="chart.h - 8" text-anchor="middle" class="axis-text"
                  >{{ p.label }}</text>
                </svg>
              </div>
            </div>
          </div>
          <div class="col-12 col-xl-5 mb-3 pl-0">
            <div class="card-style">
              <h3>Status Breakdown</h3>
              <div class="status-row" v-for="(s, i) in statusBreakdown" :key="'sb' + i">
                <span class="status-name">{{ s.label }}</span>
                <div class="status-track">
                  <div class="status-fill" :class="s.cls" :style="{ width: s.width }"></div>
                </div>
                <span class="status-count">{{ s.count }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- FILTERS -->
        <!-- Every control is labelled and sized on the same grid; the buttons have their
             own full-width column so they can no longer sit on top of each other. -->
        <div class="row wd-filters align-items-end">
          <div class="mb-2 col-6 col-md-4 col-xl-2 pl-0">
            <label>Status</label>
            <b-form-select v-model="filters.status" :options="statusOptions"></b-form-select>
          </div>
          <div class="mb-2 col-6 col-md-4 col-xl-2 pl-0">
            <label>Period</label>
            <b-form-select v-model="filters.period" :options="periodOptions"></b-form-select>
          </div>
          <template v-if="isCustomRange">
            <div class="mb-2 col-6 col-md-4 col-xl-2 pl-0">
              <label>From</label>
              <b-form-input v-model="filters.start" type="date" :max="filters.end || today"></b-form-input>
            </div>
            <div class="mb-2 col-6 col-md-4 col-xl-2 pl-0">
              <label>To</label>
              <b-form-input v-model="filters.end" type="date" :min="filters.start" :max="today"></b-form-input>
            </div>
          </template>
          <div class="mb-2 col-6 col-md-4 col-xl-2 pl-0">
            <label>Min amount ({{ currencyCode }})</label>
            <b-form-input v-model="filters.minAmount" type="number" min="0" step="0.01" placeholder="0.00"></b-form-input>
          </div>
          <div class="mb-2 col-6 col-md-4 col-xl-2 pl-0">
            <label>Max amount ({{ currencyCode }})</label>
            <b-form-input v-model="filters.maxAmount" type="number" min="0" step="0.01" placeholder="Any"></b-form-input>
          </div>
          <div class="mb-2 col-12 col-xl-4 pl-0 wd-filter-actions">
            <b-button variant="primary" @click="applyFilters">Submit</b-button>
            <b-button variant="light" @click="resetFilters">Reset</b-button>
          </div>
        </div>
        <div class="row" v-if="filterError">
          <div class="col-12 pl-0"><p class="filter-error">{{ filterError }}</p></div>
        </div>

        <!-- LIST -->
        <div class="row pl-0 mb-5">
          <div class="col-12">
            <div class="row m-0 mb-2" v-if="totalItems">
              <small class="muted">Showing {{ firstIndex }}-{{ lastIndex }} of {{ totalItems }} requests</small>
            </div>
            <b-table
              hover responsive striped bordered outlined show-empty
              :items="rows" :fields="fields"
              :table-variant="tableVariants" :head-variant="headVariant"
            >
              <template #empty>
                <div class="text-center py-3">No withdrawal requests found.</div>
              </template>
              <template #cell(index)="data">{{ data.index + 1 + offset }}.</template>
              <template #cell(bank)="data">
                <span class="bank-name">{{ bankNameOf(data.item) }}</span>
                <small class="bank-sub">{{ bankAccountOf(data.item) }}</small>
              </template>
              <template #cell(amount)="data">
                {{ currencySymbol }} {{ money(data.item.amount) }}
              </template>
              <template #cell(total_fee)="data">
                <span :class="num(feeOf(data.item)) > 0 ? 'fee-text' : ''">
                  {{ has(feeOf(data.item)) ? "- " + currencySymbol + " " + money(feeOf(data.item)) : "-" }}
                </span>
              </template>
              <template #cell(net_amount)="data">
                <strong>{{ has(data.item.net_amount) ? currencySymbol + " " + money(data.item.net_amount) : "-" }}</strong>
              </template>
              <template #cell(payout)="data">
                <span v-if="localOf(data.item)" :class="localOf(data.item).indicative ? 'is-estimate' : ''">
                  {{ localOf(data.item).text }}
                  <small v-if="localOf(data.item).indicative" class="est-tag">est.</small>
                </span>
                <span v-else class="muted">-</span>
              </template>
              <template #cell(status)="data">
                <span class="status-pill" :class="statusClass(data.item.status)">
                  {{ data.item.status_label || prettify(data.item.status) }}
                </span>
              </template>
              <template #cell(requested_on)="data">{{ formatDate(dateOf(data.item)) }}</template>
              <template #cell(remarks)="data">
                <span class="ellipsis">{{ data.item.remarks || '-' }}</span>
              </template>
              <template #cell(actions)="data">
                <a href="#" class="btn btn-primary btn-sm mr-1" @click.prevent="openDetail(data.item)">Detail</a>
                <a
                  href="#"
                  class="btn btn-danger btn-sm"
                  v-if="isCancellable(data.item)"
                  @click.prevent="askCancel(data.item)"
                >Cancel</a>
              </template>
            </b-table>

            <nav v-if="totalPages > 1">
              <ul class="pagination">
                <li class="page-item" :class="currentPage <= 1 ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage - 1)">&laquo; Previous</a>
                </li>
                <li class="page-item" v-for="i in pageWindow" :key="i" :class="currentPage === i ? 'active' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(i)">{{ i }}</a>
                </li>
                <li class="page-item" :class="currentPage >= totalPages ? 'disabled' : ''">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage + 1)">Next &raquo;</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
    <footer>
      <MerchantFooter />
    </footer>

    <!-- DETAIL -->
    <b-modal id="withdraw-detail-modal" centered size="lg" title="Withdrawal Detail" hide-footer>
      <div class="txn-detail" v-if="detailItem">
        <div class="txn-head">
          <div class="txn-amount">
            {{ currencySymbol }}
            {{ has(detailItem.net_amount) ? money(detailItem.net_amount) : money(detailItem.amount) }}
          </div>
          <span class="status-pill" :class="statusClass(detailItem.status)">
            {{ detailItem.status_label || prettify(detailItem.status) }}
          </span>
          <div class="txn-sub" v-if="has(detailItem.net_amount)">Amount your bank receives</div>
        </div>
        <div class="txn-section" v-for="(sec, si) in detailSections" :key="'ds' + si">
          <h6 class="txn-section-title">{{ sec.title }}</h6>
          <div class="txn-row" v-for="(f, i) in sec.items" :key="'df' + si + '-' + i"
               :class="f.strong ? 'is-strong' : ''">
            <span class="txn-key">{{ f.label }}</span>
            <span class="txn-val">{{ f.value || '-' }}</span>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- CANCEL -->
    <b-modal id="withdraw-cancel-modal" centered title="Cancel Withdrawal">
      <p>
        Cancel this withdrawal request of
        <strong v-if="cancelItem">{{ currencySymbol }} {{ money(cancelItem.amount) }}</strong>?
        This cannot be undone.
      </p>
      <b-form-input v-model="cancelRemarks" placeholder="Reason (optional)"></b-form-input>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="danger" :disabled="isSubmitting" @click="confirmCancel">
          {{ isSubmitting ? "Cancelling..." : "Yes, Cancel" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Keep it</b-button>
      </template>
    </b-modal>

    <b-toast id="wd-success-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Success</strong>
      </div>
      <div><p>{{ successText }}</p></div>
    </b-toast>
    <b-toast id="wd-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error</strong>
      </div>
      <div><p>{{ message }}</p></div>
    </b-toast>
  </div>
</template>
<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import MerchantHeader from "./MerchantHeader.vue";
import MerchantFooter from "./MerchantFooter.vue";
import MerchantSideNav from "./MerchantSideNav.vue";
import MenuToggle from "./MenuToggle.vue";
import { mapActions, mapState } from "vuex";
import { centsToAmount, amountToCents } from "@/utils/format";
import { convert, formatCurrency } from "@/utils/currency";

/** Our own value; the API only knows the seven real periods. */
const CUSTOM = "custom";

export default {
  components: { MerchantHeader, MerchantFooter, MerchantSideNav, MenuToggle, Loading },
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      successText: "",
      errors: {},
      perPage: 20,
      page: 1,
      form: { bank: null, amount: "", remarks: "" },
      filters: { status: "", period: "", start: "", end: "", minAmount: "", maxAmount: "" },
      filterError: "",
      quote: null,
      quoteLoading: false,
      quoteError: "",
      quoteMissing: false,   // server has no /quote endpoint - fall back to the old flow
      quoteSeq: 0,           // guards against a slow quote landing after a newer one
      quotedKey: "",         // bank+amount the current quote was priced for
      detailItem: null,
      cancelItem: null,
      cancelRemarks: "",
      tableVariants: ["light"],
      headVariant: ["light"],
      // Exactly the values the endpoint accepts. "week" used to be in this list and is
      // not one of them, so choosing it filtered nothing.
      periodOptions: [
        { value: "", text: "All Periods" },
        { value: "today", text: "Today" },
        { value: "7d", text: "Last 7 days" },
        { value: "30d", text: "Last 30 days" },
        { value: "90d", text: "Last 90 days" },
        { value: "month", text: "This Month" },
        { value: "year", text: "This Year" },
        { value: CUSTOM, text: "Custom range" },
      ],
      baseFields: [
        { key: "index", label: "S.N." },
        { key: "bank", label: "Bank" },
        { key: "amount", label: "Requested", sortable: true },
        { key: "total_fee", label: "Charges" },
        { key: "net_amount", label: "Bank Receives" },
        { key: "status", label: "Status" },
        { key: "remarks", label: "Remarks" },
        { key: "requested_on", label: "Requested On" },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  computed: {
    ...mapState([
      "merchantWithdraws", "merchantWithdrawAnalytics", "userBanks", "currencies",
      "message", "successmessage", "errormessage", "registerErrors",
      "merchantEnvironment",
    ]),

    /* -------------------- funds -------------------- */

    /** The list response carries the authoritative wallet split. */
    funds: function () { return this.withdrawState.funds || {}; },
    availableFunds: function () {
      const f = this.funds;
      if (f.available !== undefined && f.available !== null) { return f.available; }
      if (f.total !== undefined && f.total !== null) { return f.total; }
      return this.balance.amount;
    },
    currencyCode: function () {
      return this.funds.currency || this.balance.currency || "USD";
    },

    /* -------------------- filters -------------------- */

    isCustomRange: function () { return this.filters.period === CUSTOM; },
    /** `max` on the date inputs - a withdrawal cannot have happened tomorrow. */
    today: function () {
      const d = new Date();
      const p = function (n) { return (n < 10 ? "0" : "") + n; };
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
    },

    /* -------------------- settlement banks -------------------- */

    myBanks: function () {
      return Array.isArray(this.userBanks) ? this.userBanks : [];
    },
    /**
     * Primary first, so the default selection is the account the merchant nominated.
     * When nothing is registered the only option is the KYC settlement account, which the
     * API represents by simply omitting `bank` from the request.
     */
    bankOptions: function () {
      const vm = this;
      if (!this.myBanks.length) {
        return [{ value: null, text: "Settlement account on my merchant KYC" }];
      }
      const sorted = this.myBanks.slice().sort(function (a, b) {
        const ap = String(a.bank_type || "").toLowerCase() === "primary" ? 0 : 1;
        const bp = String(b.bank_type || "").toLowerCase() === "primary" ? 0 : 1;
        return ap - bp;
      });
      return sorted.map(function (b) {
        const name = (b.bank && b.bank.bank_name) || b.bank_name || "Bank";
        const type = String(b.bank_type || "").toLowerCase() === "primary" ? " (Primary)" : "";
        return { value: b.id, text: name + " - " + vm.maskAccount(b.account_number) + type };
      });
    },

    /* -------------------- quote -------------------- */

    hasQuote: function () {
      return !!(this.quote && this.quote.summary);
    },
    quoteSummary: function () {
      const q = this.quote || {};
      return Array.isArray(q.summary) ? q.summary : [];
    },
    quoteDestination: function () {
      return (this.quote && this.quote.destination) || {};
    },
    quoteBlockers: function () {
      const q = this.quote || {};
      return Array.isArray(q.blockers) ? q.blockers : [];
    },
    quoteBalanceAfter: function () {
      const b = (this.quote && this.quote.balance) || {};
      return b.after === undefined || b.after === null ? null : b.after;
    },
    /** The quote is authoritative about whether this request may be raised. */
    quoteBlocks: function () {
      return this.hasQuote && this.quote.can_proceed === false;
    },
    /**
     * Stale means the amount or bank changed after the quote was priced. Submitting a
     * stale quote is the one thing that would let the merchant confirm a figure that is
     * no longer on screen, so the button waits for the re-quote instead.
     */
    quoteStale: function () {
      return this.hasQuote && this.quotedKey !== this.quoteKey;
    },
    quoteKey: function () {
      return String(this.form.bank === null ? "" : this.form.bank) + "|" + String(this.form.amount).trim();
    },
    submitDisabled: function () {
      return !this.canWithdraw || this.isSubmitting || this.quoteLoading ||
             this.quoteBlocks || this.quoteStale;
    },

    /* -------------------- fee slabs -------------------- */

    withdrawFees: function () {
      const q = this.quote || {};
      return q.withdraw_fees || this.withdrawState.withdraw_fees || {};
    },
    /** The API pre-formats the slab bands in the merchant's display currency - use those. */
    feeSlabs: function () {
      const f = this.withdrawFees;
      const d = f.display || {};
      if (Array.isArray(d.slabs) && d.slabs.length) { return d.slabs; }
      const vm = this;
      const raw = Array.isArray(f.slabs) ? f.slabs : [];
      return raw.filter(function (sl) { return sl.is_active !== false; }).map(function (sl) {
        return {
          label: sl.label || (vm.currencySymbol + " " + vm.money(sl.min_amount) + " - " + vm.money(sl.max_amount)),
          fee_label: vm.currencySymbol + " " + vm.money(sl.fee_amount) + " fee",
        };
      });
    },
    feeDisplayCurrency: function () {
      const d = this.withdrawFees.display || {};
      return d.currency || "";
    },
    fallbackPercent: function () {
      const p = this.withdrawFees.fallback_percent;
      return p === undefined || p === null ? null : Number(p);
    },

    /* -------------------- the bank's own currency -------------------- */

    /**
     * A bank belongs to a country and the country names the currency, so the destination
     * currency is read off the saved bank:  bank.country_detail.currency.
     */
    selectedBankCurrency: function () { return this.currencyOfBank(this.form.bank); },

    /**
     * What the bank actually receives, in its own money.
     *
     * The server's payout figure wins whenever it sends one - that is the rate the payout
     * is settled at. It only sends one when a conversion applies, so a Nepali bank paid
     * out of a USD wallet comes back with payout_currency "" and nothing to show. In that
     * case the amount is converted here from the cached rate table and flagged as an
     * estimate, because it is a reference rate and not the settlement rate.
     */
    quoteLocal: function () {
      if (!this.hasQuote) { return null; }
      const q = this.quote;
      const code = this.selectedBankCurrency;
      if (!code || code === this.currencyCode) { return null; }
      if (String(q.payout_currency || (q.payout && q.payout.currency) || "").toUpperCase() === code) {
        return null;   // already listed by the server in the summary
      }
      return this.localAmount(q.net_amount, code);
    },

    /** The Payout column only earns its width once something is actually converted. */
    anyPayout: function () {
      const vm = this;
      return this.rows.some(function (r) { return !!vm.localOf(r); });
    },
    fields: function () {
      const f = this.baseFields.slice();
      if (this.anyPayout) {
        f.splice(5, 0, { key: "payout", label: "Payout" });
      }
      return f;
    },
    withdrawState: function () {
      const w = this.merchantWithdraws;
      return (w && typeof w === "object") ? w : { count: 0, limit: 20, offset: 0, filters: {}, withdraw_limits: {}, results: [] };
    },
    rows: function () { return Array.isArray(this.withdrawState.results) ? this.withdrawState.results : []; },
    totalItems: function () { return this.withdrawState.count || 0; },
    offset: function () { return this.withdrawState.offset || 0; },
    currentPage: function () { return Math.floor(this.offset / this.perPage) + 1; },
    totalPages: function () { return Math.max(1, Math.ceil(this.totalItems / this.perPage)); },
    firstIndex: function () { return this.totalItems ? this.offset + 1 : 0; },
    lastIndex: function () { return Math.min(this.offset + this.rows.length, this.totalItems); },
    pageWindow: function () {
      const total = this.totalPages, cur = this.currentPage;
      let start = Math.max(1, cur - 2);
      let end = Math.min(total, start + 4);
      start = Math.max(1, end - 4);
      const out = [];
      for (let i = start; i <= end; i++) { out.push(i); }
      return out;
    },
    withdrawLimits: function () { return this.withdrawState.withdraw_limits || {}; },
    limits: function () { return this.withdrawLimits.limits || {}; },
    usage: function () { return this.withdrawLimits.usage || {}; },
    /** Status choices come from the API (filters.available.status) when present. */
    statusOptions: function () {
      const f = this.withdrawState.filters || {};
      const available = (f.available && Array.isArray(f.available.status)) ? f.available.status : [];
      const list = available.length ? available : ["requested", "on_process", "declined", "completed", "cancelled"];
      const vm = this;
      return [{ value: "", text: "All Status" }].concat(
        list.map(function (s) { return { value: s, text: vm.prettify(s) }; })
      );
    },
    analytics: function () { return this.merchantWithdrawAnalytics || {}; },
    balance: function () { return this.analytics.balance || {}; },
    wTotals: function () { return this.analytics.totals || {}; },
    wRange: function () { return this.analytics.range || {}; },
    currencySymbol: function () {
      const c = this.balance.currency || "USD";
      return c === "USD" ? "$" : c;
    },
    completionWidth: function () {
      const r = Number(this.wTotals.completion_rate) || 0;
      return Math.max(0, Math.min(100, r)) + "%";
    },
    statusBreakdown: function () {
      const b = this.analytics.status_breakdown || {};
      const map = [
        { key: "completed", label: "Completed", cls: "is-paid" },
        { key: "requested", label: "Requested", cls: "is-pending" },
        { key: "on_process", label: "On Process", cls: "is-pending" },
        { key: "declined", label: "Declined", cls: "is-failed" },
        { key: "cancelled", label: "Cancelled", cls: "is-failed" },
      ];
      let max = 0;
      map.forEach(function (m) {
        const v = b[m.key];
        max = Math.max(max, Number(v && v.count !== undefined ? v.count : v) || 0);
      });
      return map.map(function (m) {
        const v = b[m.key];
        const count = Number(v && v.count !== undefined ? v.count : v) || 0;
        return { label: m.label, cls: m.cls, count: count, width: (max ? (count / max) * 100 : 0) + "%" };
      });
    },
    /** Highest amount the merchant may request right now, given balance + limits + usage. */
    maxRequestable: function () {
      const caps = [];
      const bal = Number(this.availableFunds);
      if (!isNaN(bal)) { caps.push(bal); }
      if (this.limits.max_amount != null) { caps.push(Number(this.limits.max_amount)); }
      if (this.limits.daily_amount != null) {
        caps.push(Number(this.limits.daily_amount) - (Number(this.usage.daily_amount) || 0));
      }
      if (this.limits.monthly_amount != null) {
        caps.push(Number(this.limits.monthly_amount) - (Number(this.usage.monthly_amount) || 0));
      }
      const valid = caps.filter(function (c) { return !isNaN(c); });
      if (!valid.length) { return null; }
      return Math.max(0, Math.min.apply(null, valid));
    },
    canWithdraw: function () { return this.blockedReason === ""; },
    blockedReason: function () {
      if (this.withdrawLimits.is_kyc_verified === false) {
        return "Your KYC must be verified before you can request a withdrawal.";
      }
      const bal = Number(this.availableFunds) || 0;
      const min = this.limits.min_amount != null ? Number(this.limits.min_amount) : 0;
      if (bal <= 0) { return "You have no available balance to withdraw."; }
      if (min && bal < min) {
        return "Your balance is below the minimum withdrawal amount of " + this.currencySymbol + " " + this.money(min) + ".";
      }
      if (this.limits.daily_count != null && Number(this.usage.daily_count) >= Number(this.limits.daily_count)) {
        return "You have reached today's withdrawal request limit.";
      }
      if (this.limits.monthly_count != null && Number(this.usage.monthly_count) >= Number(this.limits.monthly_count)) {
        return "You have reached this month's withdrawal request limit.";
      }
      if (this.maxRequestable != null && this.maxRequestable <= 0) {
        return "You have reached your withdrawal amount limit for now.";
      }
      return "";
    },
    detailSections: function () {
      const d = this.detailItem || {};
      const cur = this.currencySymbol;
      const sections = [];

      sections.push({
        title: "Request",
        items: [
          { label: "Reference", value: d.reference || d.transaction_id || (d.id ? "#" + d.id : "") },
          { label: "Settlement bank", value: this.bankNameOf(d) },
          { label: "Account", value: this.bankAccountOf(d) },
          { label: "Status", value: d.status_label || this.prettify(d.status) },
          { label: "Remarks", value: d.remarks },
          { label: "Admin Remarks", value: d.admin_remarks || d.review_remarks },
          { label: "Requested On", value: this.formatDate(this.dateOf(d)) },
          { label: "Last Updated", value: this.formatDate(d.updated_at || d.processed_at || d.reviewed_at) },
        ],
      });

      const charges = [{ label: "Requested amount", value: cur + " " + this.money(d.amount) }]
        .concat(this.chargeLines(d));
      if (this.has(d.net_amount)) {
        charges.push({ label: "Amount your bank receives", value: cur + " " + this.money(d.net_amount), strong: true });
      }
      sections.push({ title: "Charges", items: charges });

      if (this.hasPayout(d) || d.payout_currency) {
        const payout = [{ label: "Payout currency", value: d.payout_currency }];
        if (this.has(d.exchange_rate)) { payout.push({ label: "Exchange rate", value: this.rate(d.exchange_rate) }); }
        if (this.has(d.buy_rate)) { payout.push({ label: "Buy rate", value: this.rate(d.buy_rate) }); }
        if (this.has(d.sell_rate)) { payout.push({ label: "Sell rate", value: this.rate(d.sell_rate) }); }
        if (this.has(d.payout_amount)) {
          payout.push({
            label: "Payout amount",
            value: d.payout_currency + " " + this.plain(d.payout_amount),
            strong: true,
          });
        }
        sections.push({ title: "Payout", items: payout });
      } else {
        // No conversion was priced, but the bank still holds its money in its own
        // currency - show the estimate rather than nothing at all.
        const local = this.localOf(d);
        if (local && local.indicative) {
          sections.push({
            title: "Payout",
            items: [
              { label: "Bank currency", value: local.code },
              { label: "Approximate amount", value: local.text + " (estimate)", strong: true },
            ],
          });
        }
      }
      return sections;
    },
    chart: function () {
      const data = Array.isArray(this.analytics.daily) ? this.analytics.daily : [];
      const w = 720, h = 240, padL = 52, padR = 16, padT = 16, padB = 30;
      const empty = { w: w, h: h, padL: padL, padR: padR, points: [], line: "", area: "", grid: [], xLabels: [] };
      if (!data.length) { return empty; }
      const innerW = w - padL - padR, innerH = h - padT - padB, n = data.length;
      const step = n > 1 ? innerW / (n - 1) : 0;
      let maxV = 0;
      data.forEach(function (d) { maxV = Math.max(maxV, Number(d.amount) || 0); });
      const niceMax = maxV <= 0 ? 1 : Math.ceil(maxV * 1.15);
      const points = data.map(function (d, i) {
        const v = Number(d.amount) || 0;
        return {
          x: padL + (n > 1 ? i * step : innerW / 2),
          y: padT + innerH - (v / niceMax) * innerH,
          value: v, count: Number(d.count) || 0, date: d.date,
        };
      });
      const line = points.map(function (p) { return p.x.toFixed(1) + "," + p.y.toFixed(1); }).join(" ");
      const last = points[points.length - 1];
      const area = padL + "," + (padT + innerH) + " " + line + " " + last.x.toFixed(1) + "," + (padT + innerH);
      const grid = [];
      for (let g = 0; g <= 4; g++) {
        const value = (niceMax / 4) * (4 - g);
        // `value` is cents, like everything else the API sends.
        const shown = centsToAmount(value);
        grid.push({ y: padT + (innerH / 4) * g, label: shown >= 1000 ? (shown / 1000).toFixed(1) + "k" : shown.toFixed(0) });
      }
      const every = Math.max(1, Math.ceil(n / 6));
      const xLabels = [];
      points.forEach(function (p, i) {
        if (i % every === 0 || i === n - 1) {
          const parts = String(p.date).split("-");
          xLabels.push({ x: p.x, label: parts.length === 3 ? parts[2] + "/" + parts[1] : p.date });
        }
      });
      return { w: w, h: h, padL: padL, padR: padR, points: points, line: line, area: area, grid: grid, xLabels: xLabels };
    },
  },
  created() { this.loadAll(); },
  watch: {
    merchantEnvironment: function () { this.loadAll(); },
  },
  methods: {
    ...mapActions([
      "listMerchantWithdraws", "createMerchantWithdraw", "quoteMerchantWithdraw",
      "cancelMerchantWithdraw", "getMerchantWithdrawAnalytics", "listMyBanks",
      "listCurrencies",
    ]),
    currentQuery() {
      const f = this.filters;
      const custom = this.isCustomRange;
      return {
        status: f.status,
        // `custom` is ours - the API takes a period OR a start/end pair, never both.
        period: custom ? "" : f.period,
        start: custom ? f.start : "",
        end: custom ? f.end : "",
        minAmount: f.minAmount === "" ? "" : amountToCents(f.minAmount),
        maxAmount: f.maxAmount === "" ? "" : amountToCents(f.maxAmount),
        limit: this.perPage,
        offset: (this.page - 1) * this.perPage,
      };
    },
    loadAll() {
      this.isLoading = true;
      const vm = this;
      return Promise.all([
        this.listMerchantWithdraws(this.currentQuery()),
        this.getMerchantWithdrawAnalytics({}),
        this.listMyBanks(),
        this.listCurrencies(),   // cached 24h; needed to price a payout in the bank's currency
      ]).then(function () {
        vm.isLoading = false;
        if (vm.form.bank === null && vm.bankOptions.length) {
          vm.form.bank = vm.bankOptions[0].value;   // Primary, or the KYC account
        }
      }).catch(function () { vm.isLoading = false; });
    },
    reloadList() {
      this.isLoading = true;
      const vm = this;
      return this.listMerchantWithdraws(this.currentQuery())
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },
    applyFilters() {
      if (!this.validateFilters()) { return; }
      this.page = 1;
      this.reloadList();
    },
    validateFilters() {
      this.filterError = "";
      const f = this.filters;
      if (!this.isCustomRange) { return true; }
      if (!f.start && !f.end) {
        this.filterError = "Choose a From or To date, or pick a period instead.";
        return false;
      }
      if (f.start && f.end && f.start > f.end) {
        // ISO dates compare correctly as strings, so no Date parsing is needed.
        this.filterError = "The From date is after the To date.";
        return false;
      }
      return true;
    },
    resetFilters() {
      this.filters = { status: "", period: "", start: "", end: "", minAmount: "", maxAmount: "" };
      this.filterError = "";
      this.page = 1;
      this.reloadList();
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages || p === this.currentPage) { return; }
      this.page = p;
      this.reloadList();
      window.scrollTo(0, 0);
    },
    validate() {
      const e = {};
      const raw = String(this.form.amount).trim();
      const amount = Number(raw);
      // The merchant types dollars; every limit the API sends is cents. Comparing the two
      // directly is what made a $5 request look like it was under a $1 minimum.
      const cents = amountToCents(amount);
      if (!raw) { e.amount = "Amount is required."; }
      else if (isNaN(amount) || amount <= 0) { e.amount = "Enter a valid amount."; }
      else {
        if (this.limits.min_amount != null && cents < Number(this.limits.min_amount)) {
          e.amount = "Minimum withdrawal is " + this.currencySymbol + " " + this.money(this.limits.min_amount) + ".";
        } else if (this.limits.max_amount != null && cents > Number(this.limits.max_amount)) {
          e.amount = "Maximum withdrawal is " + this.currencySymbol + " " + this.money(this.limits.max_amount) + ".";
        } else if (this.maxRequestable != null && cents > this.maxRequestable) {
          e.amount = "You can request at most " + this.currencySymbol + " " + this.money(this.maxRequestable) + " right now.";
        }
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    submitRequest() {
      if (!this.canWithdraw) { return; }
      if (!this.validate()) { return; }
      this.isSubmitting = true;
      const vm = this;
      return this.createMerchantWithdraw(this.requestBody()).then(function (res) {
        if (vm.successmessage === "success") {
          vm.successText = vm.message || "Withdrawal request submitted.";
          vm.$bvToast.show("wd-success-toast");
          vm.form.amount = "";
          vm.form.remarks = "";
          vm.errors = {};
          vm.clearQuote();
          vm.page = 1;
          // The response carries the priced receipt - show it rather than making the
          // merchant hunt for the row they just created.
          const w = res && res.withdraw;
          if (w && w.id) { vm.openDetail(w); }
          return vm.loadAll();
        }
        const fe = vm.registerErrors || {};
        Object.keys(fe).forEach(function (k) { vm.$set(vm.errors, k, fe[k]); });
        vm.$bvToast.show("wd-error-toast");
      }).catch(function () {
        vm.$bvToast.show("wd-error-toast");
      }).then(function () { vm.isSubmitting = false; });
    },

    /**
     * The body for POST merchant/withdraws.
     *
     * When a fresh quote is in hand its `next_step.body` is sent back unchanged - that is
     * the whole point of quoting, and it removes any chance of the confirm request being
     * built differently from the one that was priced. Remarks are the exception: they are
     * typed after the quote, so the form's value always wins.
     */
    requestBody() {
      const next = (this.quote && this.quote.next_step) || {};
      const quoted = next.body;
      if (quoted && !this.quoteStale) {
        const body = {};
        Object.keys(quoted).forEach(function (k) { body[k] = quoted[k]; });
        body.remarks = this.form.remarks || "";
        return body;
      }
      const body = {
        amount: amountToCents(this.form.amount),
        remarks: this.form.remarks || "",
      };
      if (this.form.bank !== null && this.form.bank !== "") { body.bank = this.form.bank; }
      return body;
    },

    /* -------------------- quote -------------------- */

    onInputsChanged() {
      // b-form-select fires @change before v-model has settled, so price on the next tick.
      const vm = this;
      this.$nextTick(function () { vm.requestQuote(); });
    },
    clearQuote() {
      this.quote = null;
      this.quoteError = "";
      this.quotedKey = "";
    },
    requestQuote() {
      const amount = Number(String(this.form.amount).trim());
      if (!amount || isNaN(amount) || amount <= 0) { this.clearQuote(); return; }
      if (this.quoteMissing) { return; }         // server has no /quote - stay out of the way
      if (this.hasQuote && !this.quoteStale) { return; }   // already priced for these inputs

      const key = this.quoteKey;
      const seq = ++this.quoteSeq;
      const vm = this;
      this.quoteLoading = true;
      this.quoteError = "";
      return this.quoteMerchantWithdraw({
        amount: amountToCents(amount),
        bank: this.form.bank,
      }).then(function (res) {
        // A quote for inputs the merchant has already moved on from must not paint.
        if (seq !== vm.quoteSeq) { return; }
        vm.quoteLoading = false;
        if (res && res.ok) {
          vm.quote = res.quote;
          vm.quotedKey = key;
          return;
        }
        vm.quote = null;
        vm.quotedKey = "";
        if (res && res.missing) {
          // Older server: no pricing preview, but the request itself still works.
          vm.quoteMissing = true;
          return;
        }
        vm.quoteError = (res && res.message) || "Unable to price this withdrawal right now.";
      }).catch(function () {
        if (seq !== vm.quoteSeq) { return; }
        vm.quoteLoading = false;
        vm.quoteError = "Unable to price this withdrawal right now.";
      });
    },
    quoteRowClass(row) {
      const sign = String(row && row.sign || "").toLowerCase();
      if (sign === "fee") { return "is-fee"; }
      if (sign === "credit") { return "is-credit"; }
      if (sign === "info") { return "is-info"; }
      return "";
    },
    /** Blockers may arrive as plain strings or as { code, message } objects. */
    blockerText(b) {
      if (!b) { return ""; }
      if (typeof b === "string") { return b; }
      return b.message || b.detail || b.reason || this.prettify(b.code);
    },

    /* -------------------- row helpers -------------------- */

    /** True only for a real number - 0 counts, null and "" do not. */
    has(v) {
      if (v === null || v === undefined || v === "") { return false; }
      return !isNaN(Number(v));
    },
    /** The list calls it `fee`, the quote calls it `withdraw_fee`; total_fee covers both. */
    feeOf(row) {
      const r = row || {};
      return r.total_fee !== undefined && r.total_fee !== null ? r.total_fee : r.fee;
    },
    dateOf(row) {
      const r = row || {};
      return r.date || r.created_at || r.requested_at;
    },
    settlementOf(row) {
      const r = row || {};
      return r.settlement_bank || r.destination || {};
    },
    bankNameOf(row) {
      const sb = this.settlementOf(row);
      if (sb.bank_name) { return sb.bank_name; }
      const r = row || {};
      if (r.bank && r.bank.bank_name) { return r.bank.bank_name; }
      const id = r.bank_id !== undefined && r.bank_id !== null ? r.bank_id : r.bank;
      const found = this.myBanks.filter(function (b) { return String(b.id) === String(id); });
      if (found.length) { return (found[0].bank && found[0].bank.bank_name) || "Bank"; }
      return id ? "Bank #" + id : "-";
    },
    bankAccountOf(row) {
      const sb = this.settlementOf(row);
      if (sb.account_number_masked) { return sb.account_number_masked; }
      const r = row || {};
      const id = r.bank_id !== undefined && r.bank_id !== null ? r.bank_id : r.bank;
      const found = this.myBanks.filter(function (b) { return String(b.id) === String(id); });
      return found.length ? this.maskAccount(found[0].account_number) : "";
    },
    maskAccount(n) {
      const str = String(n || "");
      return str.length <= 4 ? str : "•••• " + str.slice(-4);
    },
    /** bank id -> the currency of the country that bank belongs to. */
    currencyOfBank(bankId) {
      if (bankId === undefined || bankId === null || bankId === "") { return ""; }
      const hit = this.myBanks.filter(function (b) { return String(b.id) === String(bankId); });
      if (!hit.length) { return ""; }
      const bank = hit[0].bank || {};
      const detail = bank.country_detail || {};
      return String(detail.currency || "").toUpperCase();
    },

    /**
     * { code, text, indicative } for an amount in wallet CENTS shown in `code`,
     * or null when there is no rate for it. Never guesses a rate.
     */
    localAmount(cents, code) {
      if (!this.has(cents) || !code) { return null; }
      const value = convert(this.currencies, centsToAmount(cents), this.currencyCode, code);
      if (value === null) { return null; }
      return {
        code: code,
        text: formatCurrency(this.currencies, value, code, { codeOnly: true }),
        indicative: true,
      };
    },

    /**
     * The destination amount for a list row: the server's payout when it priced one,
     * otherwise an estimate in the bank's own currency.
     */
    localOf(row) {
      const r = row || {};
      if (this.hasPayout(r)) {
        return {
          code: r.payout_currency,
          text: r.payout_currency + " " + this.plain(r.payout_amount),
          indicative: false,
        };
      }
      const sb = this.settlementOf(r);
      const id = sb.bank_id !== undefined && sb.bank_id !== null ? sb.bank_id : r.bank;
      const code = this.currencyOfBank(id);
      if (!code || code === this.currencyCode) { return null; }
      return this.localAmount(r.net_amount, code);
    },

    hasPayout(row) {
      const r = row || {};
      return !!r.payout_currency && this.has(r.payout_amount);
    },
    /**
     * Charge lines in deduction order. A zero fee is not a charge: a withdrawal with no
     * currency conversion still reports conversion_fee 0, and "- $ 0.00" against it reads
     * as money the merchant paid.
     */
    chargeLines(row) {
      const d = row || {};
      const cur = this.currencySymbol;
      const lines = [];
      const fee = d.fee !== undefined ? d.fee : d.withdraw_fee;
      if (this.num(fee) > 0) {
        lines.push({ label: "Withdrawal fee", value: "- " + cur + " " + this.money(fee) });
      }
      if (this.num(d.conversion_fee) > 0) {
        const pct = this.num(d.conversion_percent);
        lines.push({
          label: "Currency conversion" + (pct ? " (" + pct + "%)" : ""),
          value: "- " + cur + " " + this.money(d.conversion_fee),
        });
      }
      if (this.num(d.total_fee) > 0 && lines.length !== 1) {
        lines.push({ label: "Total charges", value: "- " + cur + " " + this.money(d.total_fee) });
      }
      if (!lines.length && this.has(d.total_fee)) {
        lines.push({ label: "Charges", value: "None" });
      }
      return lines;
    },
    isCancellable(item) {
      const s = String(item && item.status || "").toLowerCase();
      return s === "requested" || s === "on_process";
    },
    askCancel(item) {
      this.cancelItem = item;
      this.cancelRemarks = "";
      this.$bvModal.show("withdraw-cancel-modal");
    },
    confirmCancel() {
      if (!this.cancelItem) { return; }
      this.isSubmitting = true;
      const vm = this;
      return this.cancelMerchantWithdraw({
        id: this.cancelItem.id,
        remarks: this.cancelRemarks,
      }).then(function () {
        if (vm.successmessage === "success") {
          vm.successText = vm.message || "Withdrawal request cancelled.";
          vm.$bvModal.hide("withdraw-cancel-modal");
          vm.$bvToast.show("wd-success-toast");
          return vm.loadAll();
        }
        vm.$bvToast.show("wd-error-toast");
      }).catch(function () {
        vm.$bvToast.show("wd-error-toast");
      }).then(function () { vm.isSubmitting = false; });
    },
    openDetail(item) {
      this.detailItem = item;
      this.$bvModal.show("withdraw-detail-modal");
    },
    prettify(v) {
      if (!v) { return "-"; }
      return String(v).split("_").map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      }).join(" ");
    },
    statusClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "completed") { return "is-success"; }
      if (s === "requested" || s === "on_process") { return "is-pending"; }
      return "is-failed";
    },
    num(v) { const n = Number(v); return isNaN(n) ? 0 : n; },
    /**
     * EVERY plain money number from this API is in cents - balance, amounts, fees and the
     * limit values. Reading them as dollars is what showed 69576.00 for a balance of
     * 695.76. `plain()` is for the few values that are NOT cents, such as payout_amount,
     * which the API already sends in major units of the payout currency.
     */
    money(v) { return centsToAmount(v).toFixed(2); },
    plain(v) { return this.num(v).toFixed(2); },
    rate(v) {
      if (!this.has(v)) { return ""; }
      return String(parseFloat(this.num(v).toFixed(8)));
    },
    formatDate(value) {
      if (!value) { return ""; }
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.h-100 { height: 100%; }
.muted { color: #6c757d; font-size: 12px; }
.capitalize { text-transform: capitalize; }
.pending-text { color: #e08b00; }
.notice {
  background: #fdf3e0; color: #8a5a00; border-radius: 6px;
  padding: 10px 12px; font-size: 13px; margin: 0;
}
.limits { border-top: 1px solid #eef1f4; padding-top: 10px; }
.limit-row {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 13px; padding: 5px 0; border-bottom: 1px solid #f6f7f9;
}
.limit-row:last-child { border-bottom: none; }
.limit-row span { color: #6c757d; }
.rate-bar { height: 6px; background: #eef1f4; border-radius: 3px; margin-top: 8px; overflow: hidden; }
.rate-fill { height: 100%; background: #00a800; border-radius: 3px; }
.chart-wrap { width: 100%; overflow: hidden; }
.chart-svg { width: 100%; height: 240px; display: block; }
.axis-text { font-size: 10px; fill: #9aa4ae; }
.chart-empty { padding: 40px 0; text-align: center; color: #6c757d; }
.status-row { display: flex; align-items: center; margin-bottom: 10px; }
.status-name { flex: 0 0 92px; font-size: 13px; color: #6c757d; }
.status-track { flex: 1; height: 8px; background: #eef1f4; border-radius: 4px; overflow: hidden; margin: 0 10px; }
.status-fill { height: 100%; border-radius: 4px; }
.status-fill.is-paid { background: #00a800; }
.status-fill.is-pending { background: #e08b00; }
.status-fill.is-failed { background: #dc3545; }
.status-count { flex: 0 0 32px; text-align: right; font-weight: 600; font-size: 13px; }
.status-pill { font-size: 11px; padding: 2px 9px; border-radius: 10px; font-weight: 600; white-space: nowrap; }
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-pending { background: #fdf3e0; color: #e08b00; }
.status-pill.is-failed { background: #fde3e5; color: #dc3545; }
.ellipsis { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom; }
.text-danger { color: #dc3545; font-size: 12px; margin-top: 5px; display: block; }
.txn-head { border: 1px solid #e9ecef; background: #f8f9fb; border-radius: 8px; padding: 16px 18px; margin-bottom: 16px; }
.txn-amount { font-size: 26px; font-weight: 700; color: #0066b3; display: inline-block; margin-right: 12px; }
.txn-row { display: flex; justify-content: space-between; padding: 9px 2px; border-bottom: 1px solid #f4f5f7; gap: 16px; }
.txn-row:last-child { border-bottom: none; }
.txn-key { color: #6c757d; font-size: 14px; }
.txn-val { color: #1b1b1b; font-size: 14px; font-weight: 600; text-align: right; word-break: break-word; }
.pagination .page-item.disabled .page-link { pointer-events: none; opacity: 0.5; }

/* ---- filters ---- */
.wd-filters label {
  display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px; white-space: nowrap;
}
/* The merchant stylesheet makes every button 100% wide, so two of them in one column
   stacked and overlapped. They get their own row-width column and share it evenly. */
.wd-filter-actions { display: flex; gap: 10px; }
.wd-filter-actions .btn { flex: 1 1 0; min-width: 0; }
@media (min-width: 1200px) {
  .wd-filter-actions .btn { flex: 0 0 auto; min-width: 120px; }
}
.filter-error {
  background: #fdeaea; border: 1px solid #f5c2c7; border-radius: 6px;
  padding: 8px 10px; font-size: 13px; color: #b02a37; margin: 0 0 12px;
}
.funds-note { font-size: 12px; color: #6c757d; margin-top: -4px; }

/* ---- estimated (non-settlement) amounts ---- */
.is-estimate { color: #6b7280; }
.est-tag {
  font-size: 10px; color: #8a94a6; background: #f1f3f6;
  border-radius: 8px; padding: 0 6px; margin-left: 4px; white-space: nowrap;
}
.quote-row.is-local { color: #6b7280; }
.quote-row.is-local small {
  font-size: 10px; color: #8a94a6; background: #eef1f5;
  border-radius: 8px; padding: 0 6px; margin-left: 6px;
}

/* ---- quote ---- */
.quote-box {
  background: #f7f9fc; border: 1px solid #e4e9f0; border-radius: 8px;
  padding: 12px 14px; margin: 0 0 16px;
}
.quote-box.is-error {
  background: #fdeaea; border-color: #f5c2c7; color: #b02a37; font-size: 13px;
}
.quote-head {
  display: flex; align-items: baseline; flex-wrap: wrap;
  border-bottom: 1px solid #e4e9f0; padding-bottom: 8px; margin-bottom: 8px;
}
.quote-title { font-size: 13px; font-weight: 700; color: #2c3e50; margin-right: 10px; }
.quote-dest { font-size: 12px; color: #6c757d; }
.quote-row {
  display: flex; justify-content: space-between; gap: 16px;
  font-size: 13px; color: #6c757d; padding: 4px 0;
}
.quote-row .quote-value { white-space: nowrap; font-variant-numeric: tabular-nums; }
.quote-row.is-fee .quote-value { color: #dc3545; }
.quote-row.is-credit {
  color: #2c3e50; font-weight: 700; font-size: 15px;
  border-top: 1px solid #e4e9f0; margin-top: 4px; padding-top: 8px;
}
.quote-row.is-info { font-size: 12px; color: #8a94a6; }
.quote-foot { font-size: 12px; color: #6c757d; margin-top: 8px; }
.quote-blockers {
  margin-top: 10px; background: #fdeaea; border: 1px solid #f5c2c7;
  border-radius: 6px; padding: 8px 10px; font-size: 12px; color: #b02a37;
}

/* ---- fee slabs ---- */
.slabs { border-top: 1px solid #eef1f4; padding-top: 10px; }
.slabs-head { font-size: 12px; font-weight: 700; color: #2c3e50; margin-bottom: 6px; }
.slab-row {
  display: flex; justify-content: space-between; gap: 12px;
  font-size: 12px; color: #6c757d; padding: 3px 0;
}
.slab-row strong { color: #2c3e50; white-space: nowrap; }
.slab-foot { font-size: 11px; color: #8a94a6; margin-top: 6px; }

/* ---- list ---- */
.bank-name { display: block; font-size: 13px; }
.bank-sub { display: block; color: #8a94a6; font-size: 11px; }
.fee-text { color: #dc3545; }

/* ---- detail sections ---- */
.txn-sub { color: #6c757d; font-size: 13px; margin-top: 6px; }
.txn-section { margin-bottom: 14px; }
.txn-section-title {
  font-size: 12px; text-transform: uppercase; letter-spacing: .4px;
  color: #8a94a6; margin-bottom: 6px;
}
.txn-row.is-strong .txn-key, .txn-row.is-strong .txn-val { font-weight: 700; color: #1b1b1b; }
</style>
